import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const ReportsPage = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedProjectData, setSelectedProjectData] = useState(null);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  
  // New state for Excel upload functionality
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  const navigate = useNavigate();

  // Fetch report data
  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`https://crd-training.in/api/v1/project/report`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        }
      });
      
      const responseData = await response.json();
      
      if (responseData.data && responseData.data.length > 0) {
        setReportData(responseData.data);
        setError(null);
      } else {
        setError(responseData.message || 'No data available');
        setReportData([]);
      }
    } catch (err) {
      setError('Network error: ' + err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tmp list data - REMOVED (not needed)

  // Fetch last updated date
  const fetchLastUpdated = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`https://crd-training.in/api/v1/tmp/last-updated`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        }
      });
      
      const responseData = await response.json();
      
      if (!responseData.error && responseData.data) {
        setLastUpdated(responseData.data.created_at || null);
      }
    } catch (err) {
      console.error('Error fetching last updated:', err);
    }
  };

  // Upload Excel file
  const uploadExcelFile = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      setUploadSuccess(false);
      
      const authToken = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await fetch(`https://crd-training.in/api/v1/tmp`, {
        method: 'POST',
        headers: {
          'Authorization': authToken
        },
        body: formData
      });
      
      const responseData = await response.json();
      
      if (response.ok && responseData.success) {
        setUploadSuccess(true);
        setSelectedFile(null);
        // Refresh the last updated after successful upload
        await fetchLastUpdated();
        // Optionally refresh the main report data
        await fetchReportData();
        
        // Auto close modal after 2 seconds
        setTimeout(() => {
          setShowUploadModal(false);
          setUploadSuccess(false);
        }, 2000);
      } else {
        setUploadError(responseData.message || 'Upload failed');
      }
    } catch (err) {
      setUploadError('Network error: ' + err.message);
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type (Excel files)
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel' // .xls
      ];
      
      if (allowedTypes.includes(file.type)) {
        setSelectedFile(file);
        setUploadError(null);
      } else {
        setUploadError('Please select a valid Excel file (.xlsx or .xls)');
        setSelectedFile(null);
      }
    }
  };

  // Open upload modal
  const openUploadModal = async () => {
    setShowUploadModal(true);
    setUploadError(null);
    setUploadSuccess(false);
    setSelectedFile(null);
    // Fetch current last updated when opening modal
    await fetchLastUpdated();
  };

  // Close upload modal
  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadError(null);
    setUploadSuccess(false);
    setSelectedFile(null);
  };
// Export to Excel function (CSV format)
const exportToExcel = () => {
  if (filteredData.length === 0) {
    alert('No data available to download');
    return;
  }

  try {
    const headers = [
      'Sl.No',
      'Type',
      'Institute',
      'Scheme', 
      'Year',
      'Short Title',
      'Code',
      'Title',
      
      'Completed Centers',
      'Completed Count',
      'Pending Centers',
      'Pending Count',
      'Total Centers'
    ];

    const csvRows = [headers.join(',')];

    filteredData.forEach((item, index) => {
      // Process completed and pending centers
      const completedCenters = Array.isArray(item.completed) ? item.completed.join('; ') : '-';
      const pendingCenters = Array.isArray(item.pending) ? item.pending.join('; ') : '-';
      const completedCount = Array.isArray(item.completed) ? item.completed.length : 0;
      const pendingCount = Array.isArray(item.pending) ? item.pending.length : 0;
      const totalCenters = completedCount + pendingCount;

      const row = [
        index + 1,
        `"${(item.type || '-').replace(/"/g, '""')}"`,
        `"${(item.institute || '-').replace(/"/g, '""')}"`,
        `"${(item.scheme || '-').replace(/"/g, '""')}"`,
        `"${(item.year || '-').replace(/"/g, '""')}"`,
        `"${(item.stitle || '-').replace(/"/g, '""')}"`,
        `"${(item.code || '-').replace(/"/g, '""')}"`,
        `"${(item.title || '-').replace(/"/g, '""')}"`,
        `"${(item.ids || '-').replace(/"/g, '""')}"`,
        `"${completedCenters.replace(/"/g, '""')}"`,
        completedCount,
        `"${pendingCenters.replace(/"/g, '""')}"`,
        pendingCount,
        totalCenters
      ];
      csvRows.push(row.join(','));
    });

    // Create CSV content with BOM for proper Excel encoding
    const csvContent = '\uFEFF' + csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Create filename with current date and filter type
    const currentDate = new Date().toISOString().split('T')[0];
    const typeFilter = selectedType === 'all' ? 'All' : selectedType.charAt(0).toUpperCase() + selectedType.slice(1);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `Reports_${typeFilter}_${currentDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    alert('Error exporting data to CSV. Please try again.');
  }
};
  // Fetch selected report data when clicking on completed count
  const fetchSelectedReportData = async (ids) => {
    try {
      setLoadingSelected(true);
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`https://crd-training.in/api/v1/project/report/selected?ids=${ids}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        }
      });
      
      const responseData = await response.json();
      
      if (responseData.data && responseData.data.length > 0) {
        setSelectedProjectData(responseData);
        setShowModal(true);
      } else {
        alert(responseData.message || 'No data available');
      }
    } catch (err) {
      alert('Network error: ' + err.message);
      console.error('Error:', err);
    } finally {
      setLoadingSelected(false);
    }
  };

  // Handle completed count click
  const handleCompletedClick = (ids) => {
    if (ids) {
      fetchSelectedReportData(ids);
    }
  };

// Handle district wise navigation
const handleDistrictWiseClick = () => {
  navigate('/district-report');
};

// Handle report formats navigation
const handleReportFormatsClick = () => {
  navigate('/report-format');
};
  // Toggle expanded state for a row
  const toggleExpanded = (code) => {
    setExpandedRows(prev => ({
      ...prev,
      [code]: !prev[code]
    }));
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedProjectData(null);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchReportData();
    fetchLastUpdated();
  }, []);

  // Calculate statistics
  const calculateStats = () => {
    const filteredData = getFilteredData();
    const stats = {
      totalProjects: filteredData.length,
      totalCompleted: 0,
      totalPending: 0,
      totalMale: 0,
      totalFemale: 0,
      totalAmountSpent: 0,
      institutes: new Set(),
      schemes: new Set()
    };

    filteredData.forEach(item => {
      stats.totalCompleted += item.completed.length;
      stats.totalPending += item.pending.length;
      stats.institutes.add(item.institute);
      stats.schemes.add(item.scheme);
    });

    return stats;
  };

  // Filter data based on selected type
  const getFilteredData = () => {
    if (selectedType === 'all') {
      return reportData;
    }
    return reportData.filter(item => {
      return item.type === selectedType;
    });
  };

  // Calculate totals for attendance and amount spent from batch data
  const calculateBatchTotals = (batchesData) => {
    let totalMale = 0;
    let totalFemale = 0;
    let totalAmountSpent = 0;

    if (batchesData && Array.isArray(batchesData)) {
      batchesData.forEach(batch => {
        if (batch.attendance) {
          totalMale += batch.attendance.male || 0;
          totalFemale += batch.attendance.female || 0;
        }
        if (batch.amount_spent) {
          totalAmountSpent += parseInt(batch.amount_spent) || 0;
        }
      });
    }

    return {
      totalMale,
      totalFemale,
      totalAmountSpent
    };
  };

  // Get modal type from selected project data
  const getModalType = () => {
    if (!selectedProjectData || !selectedProjectData.data || selectedProjectData.data.length === 0) {
      return 'training';
    }
    return selectedProjectData.data[0].type?.toLowerCase() || 'training';
  };

  const filteredData = getFilteredData();
  const stats = calculateStats();

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Reports</h1>
          {lastUpdated && (
            <p className="text-sm text-gray-600 mt-1">
              Last updated: {new Date(lastUpdated).toLocaleString('en-IN')}
            </p>
          )}
        </div>
<div className="flex gap-4 items-center">
 
          {/* View District Wise Button */}
          <button
            onClick={handleDistrictWiseClick}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            View Center Wise
          </button>

          {/* Export to Excel Button */}
          {filteredData.length > 0 && (
            <button
              onClick={exportToExcel}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors duration-200 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export to Excel
            </button>
          )}

          {/* View Report Formats Button */}
          {/* <button
            onClick={handleReportFormatsClick}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01-2 2z" />
            </svg>
            View Report Formats
          </button> */}
          
          {/* Type Filter Dropdown */}
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="training">Training</option>
            <option value="infra">Infrastructure</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-blue-600"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          <p className="font-medium">Error: {error}</p>
          <button
            onClick={fetchReportData}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Main Table */}
      {!loading && filteredData.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="border border-gray-300 px-4 py-2 text-left">S.No</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Institute</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Scheme</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Code</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Title</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Year</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Type</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Completed</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Pending</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <React.Fragment key={`${item.code}-${index}`}>
                  <tr className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">{index + 1}</td>
                    <td className="border border-gray-300 px-4 py-2">{item.institute}</td>
                    <td className="border border-gray-300 px-4 py-2">
                      <div className="max-w-xs">
                        <p className="truncate" title={item.scheme}>{item.scheme}</p>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                        {item.code}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {item.title || <span className="text-gray-400 italic">No title</span>}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{item.year}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        item.type === 'training' ? 'bg-blue-100 text-blue-800' :
                        item.type === 'infra' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      <button
                        onClick={() => handleCompletedClick(item.ids)}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
                        title="Click to view details"
                      >
                        {item.completed.length}
                      </button>
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        {item.pending.length}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      <button
                        onClick={() => toggleExpanded(item.code)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        {expandedRows[item.code] ? 'Hide' : 'Show'} Details
                      </button>
                    </td>
                  </tr>
                  {expandedRows[item.code] && (
                    <tr>
                      <td colSpan="10" className="border border-gray-300 bg-gray-50">
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-semibold text-green-700 mb-2">
                                Completed Districts ({item.completed.length})
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {item.completed.map((district, idx) => (
                                  <span
                                    key={`${district}-${idx}`}
                                    className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs"
                                  >
                                    {district}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h4 className="font-semibold text-amber-700 mb-2">
                                Pending Districts ({item.pending.length})
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {item.pending.map((district, idx) => (
                                  <span
                                    key={`${district}-${idx}`}
                                    className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs"
                                  >
                                    {district}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Excel Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Upload Excel File</h3>
              <button
                onClick={closeUploadModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            {/* Last Updated Info */}
            {lastUpdated && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Last Updated:</span> {new Date(lastUpdated).toLocaleString('en-IN')}
                </p>
              </div>
            )}
            
            {/* File Upload Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Excel File (.xlsx or .xls)
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                disabled={uploading}
              />
              {selectedFile && (
                <p className="mt-2 text-sm text-green-600">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
            
            {/* Upload Error */}
            {uploadError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                <p className="text-sm">{uploadError}</p>
              </div>
            )}
            
            {/* Upload Success */}
            {uploadSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded">
                <p className="text-sm font-medium">File uploaded successfully!</p>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={closeUploadModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={uploadExcelFile}
                disabled={!selectedFile || uploading}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-t-2 border-white"></div>
                )}
                {uploading ? 'Uploading...' : 'Upload File'}
              </button>
            </div>
          </div>
        </div>
      )}

{/* Modal for Selected Project Data */}
{showModal && selectedProjectData && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-5/6 lg:w-4/5 shadow-lg rounded-md bg-white">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold">Details</h3>
          {selectedProjectData.message && (
            <p className="text-sm text-gray-600 mt-1">{selectedProjectData.message}</p>
          )}
        </div>
        <button
          onClick={closeModal}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      {loadingSelected ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-t-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {selectedProjectData.data && selectedProjectData.data.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-300 px-3 py-2 text-left">S.No</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">Center</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">Type</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">Title</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">Training Type</th>
                      {/* Show Participants and Batches only for Training */}
                      {getModalType() === 'training' && (
                        <>
                          <th className="border border-gray-300 px-3 py-2 text-center">Participants</th>
                          <th className="border border-gray-300 px-3 py-2 text-center">Batches</th>
                        </>
                      )}
                      <th className="border border-gray-300 px-3 py-2 text-right">Budget</th>
                      {/* Show Spent only for Infra */}
                      {getModalType() === 'infra' && (
                        <th className="border border-gray-300 px-3 py-2 text-right">Spent</th>
                      )}
                      <th className="border border-gray-300 px-3 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProjectData.data.map((item, index) => {
                      const batchTotals = calculateBatchTotals(item.batchesData);
                      return (
                        <React.Fragment key={item.id}>
                          <tr className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-3 py-2">{index + 1}</td>
                            <td className="border border-gray-300 px-3 py-2">
                              <div className="font-medium">{item.center_name}</div>
                              <div className="text-xs text-gray-500">({item.center})</div>
                            </td>
                            <td className="border border-gray-300 px-3 py-2">{item.type}</td>
                            <td className="border border-gray-300 px-3 py-2">
                              <div>{item.title}</div>
                              <div className="text-xs text-gray-500">{item.scheme_name}</div>
                            </td>
                            <td className="border border-gray-300 px-3 py-2">{item.training_type}</td>
                            {/* Show these columns only for Training */}
                            {getModalType() === 'training' && (
                              <>
                                <td className="border border-gray-300 px-3 py-2 text-center">
                                  {item.participants || '-'}
                                </td>
                                <td className="border border-gray-300 px-3 py-2 text-center">
                                  {item.batches || '-'}
                                </td>
                              </>
                            )}
                            <td className="border border-gray-300 px-3 py-2 text-right font-mono">
                              ₹{parseInt(item.amount || 0).toLocaleString('en-IN')}
                            </td>
                            {/* Show Spent only for Infra */}
                            {getModalType() === 'infra' && (
                              <td className="border border-gray-300 px-3 py-2 text-right font-mono">
                                {batchTotals.totalAmountSpent > 0 ? (
                                  <span className="text-green-600 font-medium">
                                    ₹{batchTotals.totalAmountSpent.toLocaleString('en-IN')}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            )}
                            <td className="border border-gray-300 px-3 py-2 text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                item.status === 'Approved' ? 'bg-green-100 text-green-800' : 
                                item.status === 'Active' ? 'bg-blue-100 text-blue-800' : 
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {item.status}
                              </span>
                            </td>
                          </tr>
                          
                          {/* Batch Details Row - Only show for Training type */}
                          {getModalType() === 'training' && item.batchesData && item.batchesData.length > 0 && (
                            <tr>
                              <td colSpan="9" className="border border-gray-300 bg-gray-50 p-4">
                                <div>
                                  <h5 className="font-semibold text-gray-700 mb-2">Batch Details</h5>
                                  <table className="w-full">
                                    <thead>
                                      <tr className="text-left text-sm bg-gray-100">
                                        <th className="px-2 py-1 border border-gray-300">Batch No</th>
                                        <th className="px-2 py-1 border border-gray-300">From Date</th>
                                        <th className="px-2 py-1 border border-gray-300">To Date</th>
                                        <th className="px-2 py-1 border border-gray-300 text-center">Male</th>
                                        <th className="px-2 py-1 border border-gray-300 text-center">Female</th>
                                        <th className="px-2 py-1 border border-gray-300 text-right">Amount Spent</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {item.batchesData.map((batch, batchIdx) => (
                                        <tr key={batchIdx} className="text-sm">
                                          <td className="px-2 py-1 border border-gray-300">{batch.batchNo}</td>
                                          <td className="px-2 py-1 border border-gray-300">{new Date(batch.fromDate).toLocaleDateString('en-IN')}</td>
                                          <td className="px-2 py-1 border border-gray-300">{new Date(batch.toDate).toLocaleDateString('en-IN')}</td>
                                          <td className="px-2 py-1 border border-gray-300 text-center">
                                            <span className={batch.attendance?.male ? 'text-blue-600 font-medium' : 'text-gray-400'}>
                                              {batch.attendance?.male || '-'}
                                            </span>
                                          </td>
                                          <td className="px-2 py-1 border border-gray-300 text-center">
                                            <span className={batch.attendance?.female ? 'text-pink-600 font-medium' : 'text-gray-400'}>
                                              {batch.attendance?.female || '-'}
                                            </span>
                                          </td>
                                          <td className="px-2 py-1 border border-gray-300 text-right font-mono">
                                            {batch.amount_spent ? (
                                              <span className="text-green-600 font-medium">
                                                ₹{parseInt(batch.amount_spent).toLocaleString('en-IN')}
                                              </span>
                                            ) : (
                                              <span className="text-gray-400">-</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                
                                {/* Clientile Details */}
                                {item.clientile && item.clientile.length > 0 && (
                                  <div className="mt-4">
                                    <h5 className="font-semibold text-gray-700 mb-2">Clientile Details</h5>
                                    <div className="space-y-2">
                                      {item.clientile.map((client, clientIdx) => (
                                        <div key={clientIdx} className="text-sm bg-white p-2 rounded border border-gray-200">
                                          <span className="font-medium">{client.typeLabel}</span> - 
                                          <span className="ml-2">{client.member}</span>
                                          <span className="ml-2 text-gray-600">({client.nos} participants)</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )}
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No data available</p>
            </div>
          )}
        </>
      )}
      
      <div className="mt-4 flex justify-end">
        <button
          onClick={closeModal}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

      {/* No Data State */}
      {!loading && filteredData.length === 0 && !error && (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="mt-2 text-gray-600">
            {selectedType === 'all' 
              ? 'No report data available' 
              : `No ${selectedType} report data available`}
          </p>
          <button 
            onClick={fetchReportData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Fetch Reports
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;